import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  testLocalStorage,
  dispatchSdKEvent,
  setSdkData,
} from "@root/_utils";

import {
  getInitialVariants,
  getInitialFormData,
  getShippingLeadTime,
  Upload,
} from "@proxy/_utils";



export const FormSchema = {
  id: "",
  properties: {
    _gcp_selected_card_image: "",
    _gcp_custom_value: "",
    _gcp_send_to_self: false,
    _gcp_recipient_email: "",
    _gcp_recipient_phone: "",
    _gcp_recipient_name: "",
    _gcp_delivery_time: "Send Now",
    _gcp_delivery_date: "",
    _gcp_delivery_timezone: "",
    _gcp_iso_delivery_date: "",
    _gcp_message_from: "",
    _gcp_message_text: "",
    _gcp_message_salutation: "",
    _gcp_message_font_family: null,
    _gcp_signature_font_family: null,
    _gcp_thumbnail_image: "",
    _gcp_locale: typeof window.Shopify != 'undefined' && window.Shopify.locale ? window.Shopify.locale : "",
    _gcp_conversion_rate: typeof window.Shopify != 'undefined' && window.Shopify.currency.rate ? window.Shopify.currency.rate : "",
    _gcp_fulfillment_type: "",
    _gcp_formatted_address: "",
    _gcp_video_id: "",
    _gcp_address: {} as any,
  },
  fields: {
    _gcp_helper_tabIndex: 0,
    _gcp_helper_video: "",
  },
};

const useGiftCardFormManager = ({
  loading,
  settings,
  preview,
  pricingConfig,
  inDashboard,  
  updateFormErrors,
  fulfillmentType,
  globalVars,
}: any) => {

  const formRef = useRef(null);

  const [addToCartSuccess, setAddToCartSuccess]     = useState(false);
  const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);
  const [enableCamera, setEnableCamera]             = useState(false);
  const [maxLinesReached, setMaxLinesReached]       = useState(false);
  const [formData, setFormData]                     = useState(FormSchema);
  const [deliveryLeadTime, setDeliveryLeadTime]     = useState(0);

  /**
   * Submit the purchase form
   * @param {*} event 
   * @returns 
   */
  const submitForm = (event: Event) => {
    // prevent the default form action to happen
    event.preventDefault();

    const currentForm: any = formRef.current;
    // get the submit button by id
    const submitButton = document?.getElementById("gcpPurchaseFormSubmit");
    // if already being submitted
    if (submitButton?.classList.contains('loading')) return;

    // add loading state and disable the submit button
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');

    if (!currentForm || inDashboard) {
      return;
    }

    // if custom value is set to true, submit the form normally to draft order create endpoint
    if (formData.id === 'Custom') {
      setAddToCartModalOpen(true);
      currentForm.submit();
      return;
    } else {

      // set the configurations to submit the form
      const config = {
        method: 'POST',
        headers: {
          'Accept': `application/json`,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(currentForm)
      };

      fetch('/cart/add', config)
      .then((response) => response.json())
      .then((response) => {

        if (response.status || response.errors) {
          setAddToCartSuccess(false);
          return;
        }
        dispatchSdKEvent('gcp-item-added');
        setAddToCartSuccess(true);
      })
      .catch((e) => {
        console.error(e);
        setAddToCartSuccess(false);
        return;
      })
      .finally(() => {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        // show modal
        setAddToCartModalOpen(true);
      });
    }
  };

   /**
   * Update a form field
   * @param {string} field
   * @param {string} data
   * @param {boolean} fromSDK
   */
   const updateFormField = (
    field: any, 
    data: any, 
    fromSDK = false
  ) => {

    if (!settings || !Object.keys(settings).length || !field || !formData || !fulfillmentType) {
      console.warn("Unable to update field", {
        settings,
        field,
        formData,
        fulfillmentType
      })
      return;
    }

    const newData: any = { ...formData};
    let fieldParts = field.split(".");
    
    if (fieldParts.length === 3) {
      const newNestedData = { ...newData[fieldParts[0]][fieldParts[1]], [fieldParts[2]]: data };
      newData[fieldParts[0]][fieldParts[1]] = newNestedData;
    } else if (fieldParts.length === 2) {
      newData[fieldParts[0]][fieldParts[1]] = data;
    } else {
      newData[fieldParts[0]] = data;
    }

    const localStorageKey = fulfillmentType === "physical" ? "gcp_form_data_physical" : "gcp_form_data";

    if (testLocalStorage() === true) {
      localStorage.setItem(localStorageKey, JSON.stringify(newData));
    }
    
    if (fromSDK === false) {
      dispatchSdKEvent('gcp-field-changed', {
        'detail': {
          field: field,
          value: data
        }
      });
    }

    setFormData(newData);

    setSdkData('form', newData);
    
    dispatchSdKEvent('gcp-form-changed');

  };

  /**
   * Handle the recording completion
   * @param {string} videoUrl
   * @param {boolean} isMobile
   */
  const handleRecordingComplete = async (
    videoUrl: string, 
    isMobile: boolean
  ) => {

    const videoUpload = await new Upload(videoUrl);

    if (videoUpload.error && typeof(videoUpload.error) == "string") {
      updateFormErrors({
        _gcp_video_id: {
          error: true,
          message: videoUpload.error
        }
      });

      if (isMobile == false) closeCameraModal();

      return;
    }

    updateFormField("properties._gcp_video_id", videoUpload.id);

    updateFormField("fields._gcp_helper_video", videoUpload.url);

    if (typeof(formData.properties._gcp_thumbnail_image) == "string" && formData.properties._gcp_thumbnail_image.length == 0) return;

    const imageUpload = await new Upload(formData.properties._gcp_thumbnail_image);

    // Upload errors can be Error objects, not only strings
    if (imageUpload.error) {
      
      updateFormErrors({
        _gcp_thumbnail_image: {
          error: true,
          message: typeof(imageUpload.error) == "string" ? imageUpload.error : "Something went wrong, please try again"
        }
      });

      updateFormField("properties._gcp_thumbnail_image", "");

      if (isMobile == false) closeCameraModal();

      return;
    }

    updateFormField("properties._gcp_thumbnail_image", imageUpload.url);

    if (isMobile == false) closeCameraModal();

  }

  /**
   * Update the allowed line count for a gift message
   */
  const updateLineCount = useCallback(() => {

    const messageSizingContraint = document.querySelector('.gcp__message-sizing-contraint-container')?.clientHeight;
    const messageSizingContent   = document.querySelector('.gcp__message-sizing-content')?.clientHeight;
    const buffer = 400;

    if (messageSizingContent && messageSizingContraint && (messageSizingContent + buffer) > messageSizingContraint) {
      setMaxLinesReached(true);
    } else {
      setMaxLinesReached(false);
    }
  }, [maxLinesReached]);

  /**
   * When user changes between physical and digital, load respective form data from localStorage
   */
  const loadFormData = (
  ) => {

    if (!settings || !settings.product || !fulfillmentType) {
      return;
    }

    const initialProducts = getInitialVariants(settings, preview);
    const newData: any    = getInitialFormData(FormSchema, initialProducts, settings, preview, fulfillmentType);
    
    if (testLocalStorage() === true) {
      const localStorageKey = fulfillmentType === "physical" ? "gcp_form_data_physical" : "gcp_form_data";
      localStorage.setItem(localStorageKey, JSON.stringify(newData));
    }

    newData.properties._gcp_fulfillment_type = fulfillmentType;

    setFormData({...newData});
    

    setSdkData('form', newData);
    dispatchSdKEvent('gcp-form-changed');

  };

  /**
   * Close the camera modal
   */
  const closeCameraModal = () => {
    setEnableCamera(false);
    window.MicroModal.close("gcpVideoRecordingModal");
  };


  /**
   * Builds the _gcp_iso_delivery_date when _gcp_delivery_date or _gcp_delivery_timezone change
   */
  useEffect(useCallback(() => {

    const date = formData.properties._gcp_delivery_date;
    const timezone = formData.properties._gcp_delivery_timezone;
    
    if (date && date.length !== 0 && timezone && timezone.length !== 0 && settings && settings.product !== undefined) {
      const requestedValue = date + ":00" + timezone;
      updateFormField("properties._gcp_iso_delivery_date", requestedValue);
    }

  }, [formData]), [formData.properties._gcp_delivery_date, formData.properties._gcp_delivery_timezone]);

  /**
   * When the form data changes, update the locale
   * if it is different from the current locale
   */
  useLayoutEffect(useCallback(() => {

    if (typeof(window.Shopify) != 'undefined' && window.Shopify.locale !== formData.properties._gcp_locale) {
      updateFormField("properties._gcp_locale", window.Shopify.locale || "");
    }

  },[formData]));


  useEffect(() => {

    loadFormData();

  }, [fulfillmentType, settings]);

  useEffect(() => {

    const country = formData?.properties?._gcp_address?.country;
    const shippingLeadTimes = globalVars?.shippingLeadTimes;
    const leadTime = getShippingLeadTime(fulfillmentType, country, shippingLeadTimes);

    setDeliveryLeadTime(leadTime);

  }, [fulfillmentType, formData.properties?._gcp_address?.country, globalVars?.shippingLeadTimes]);

  const res = {
    handleRecordingComplete,
    submitForm,
    addToCartSuccess,
    addToCartModalOpen,
    setAddToCartModalOpen,
    maxLinesReached,
    setMaxLinesReached,
    formData,
    updateFormField,
    updateLineCount,
    enableCamera,
    setEnableCamera,
    closeCameraModal,
    formRef,
    deliveryLeadTime,
  };

  return useMemo(() => res, [res]);

}

export default useGiftCardFormManager;